import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ItemConsumptionDTO, LocationDTO, OrderDTO, OrderStatus, ProjectDTO2, WorkTaskStatus } from '../../../../api/api';
import { AddIcon } from '../../../../assets/icons/AddIcon';
import {
  ContentFooterContainer,
  Footer,
  FooterLeft,
  FooterRight,
  TabProps
} from '../../../../blocks/tabs-vertical/TabsVertical';
import Button from '../../../../components/button/Button';
import LoadingOverlay from '../../../../components/loading-overlay/LoadingOverlay';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '../../../../components/table/Table';
import Typography from '../../../../components/typography/Typography';
import GoodsService from '../../../../services/GoodsService';
import TaskService from '../../../../services/TaskService';
import { log } from '../../../../utils/logging/log';
import GoodsTableRow from '../components/GoodsTableRow';
import NoDataTableRow from '../components/NoDataTableRow';
import ConsumeGoodsFromCarPage from './ConsumeGoodsFromCarPage';
import { ShopIcon } from '../../../../assets/icons/ShopIcon';
import { useDispatch, useSelector } from 'react-redux';
import {
  setWorkTaskOrderInfo,
  selectWorkTaskOrderInfo,
  setOrderGoodsList,
  setSelectedDeliveryLocation,
  setDeliveryDate,
  setNotes,
  setSelectedOrderReceipt,
  selectSelectedOrderReceipt,
  setShowOrderGoods
} from '../../../../stateManagement/reducers/orderGoodsReducer';
import DialogViewPageHeader from '../../../../components/dialog-view-page-header/DialogViewPageHeader';
import useWorkTaskOrdersTable from './useWorkTaskOrdersTable';
import TableClient from '../../../../blocks/table/TableClient';
import OrderReceiptContent from '../../../goods-view/goods-view-tabs/orders-tab/order-receipt/OrderReceiptContent';
import StatusTag from '../../../../components/status-tag/StatusTag';
import { getEnumDisplayValue } from '../../../../utils/enumUtils';
import { userAllowedThroughFeatureFlag } from '../../../../utils/validation/userAllowedFeatureFlag';
import ConsumeGoodsPage from './ConsumeGoodsPage';
import Alert from '../../../../components/alert/Alert';
import OrdersService from '../../../../services/OrdersService';
import WarehouseService from '../../../../services/WarehouseService';
import NotificationService from '../../../../services/NotificationService';

enum GoodsPage {
  STANDARD = 'standard',
  ADD = 'add',
  RECEIPT = 'receipt',
  CONSUME = 'consume'
}

type Page = GoodsPage;

interface Props extends TabProps {
  taskStatus?: WorkTaskStatus;
  taskId: string;
  projectNumber: string;
  projectLegalEntityId: string;
  taskLocation?: LocationDTO;
}

const TaskGoodsStep = (props: Props) => {
  const { taskId, projectNumber, projectLegalEntityId, taskLocation } = props;
  const [viewState, setViewState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [page, setPage] = useState<Page>(GoodsPage.STANDARD);
  const [consumedItems, setConsumedItems] = useState<ItemConsumptionDTO[]>([]);
  const [orders, setOrders] = useState<OrderDTO[]>([]);
  const [project, setProject] = useState<ProjectDTO2>();
  const [disableConsumeOrderButton, setDisableConsumeOrderButton] = useState(true);
  const [disableOrderGoodsButton, setDisableOrderGoodsButton] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    WarehouseService.canOrderGoods(projectNumber)
      .then((res) => {
        setDisableOrderGoodsButton(!res);
      })
      .catch((e) => {
        log(e);
        NotificationService.error('Kunne ikke hente projekt stadie');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [projectNumber]);

  const selectedWorkTaskOrderInfo = useSelector(selectWorkTaskOrderInfo);
  const order = useSelector(selectSelectedOrderReceipt);

  const dispatch = useDispatch();

  const load = useCallback(() => {
    if (taskId) {
      const consumedItemsPromise = GoodsService.getConsumedItems(parseInt(taskId)).then((res) => setConsumedItems(res));
      const projectPromise = TaskService.getProjectById(projectNumber).then((res) => setProject(res));
      const workOrderPromise = GoodsService.getActiveOrdersByWorkTask(parseInt(taskId)).then((_orders) => {
        setOrders(_orders);
        const promises = _orders.map((order) => order.orderId && OrdersService.isOrderValidForConsumption(order.orderId));
        Promise.all(promises).then((responses) => setDisableConsumeOrderButton(responses.every((response) => !response)));
      });

      Promise.all([consumedItemsPromise, projectPromise, workOrderPromise])
        .then(() => setViewState('ready'))
        .catch((error) => {
          log(error);
          setViewState('error');
        });
    }
  }, [projectNumber, taskId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleOpenOrderGoods = () => {
    if (selectedWorkTaskOrderInfo?.workTaskId !== taskId) {
      dispatch(setOrderGoodsList([]));
      dispatch(setSelectedDeliveryLocation({ id: '', name: '' }));
      dispatch(setDeliveryDate(''));
      dispatch(setNotes(''));
      dispatch(setSelectedOrderReceipt(undefined));
    }
    dispatch(
      setWorkTaskOrderInfo({
        workTaskId: taskId,
        projectNumber: projectNumber,
        projectLegalEntityId: projectLegalEntityId,
        address: taskLocation
      })
    );

    dispatch(setShowOrderGoods(true));
  };

  const openOrderReceipt = useCallback(
    (order: OrderDTO) => {
      dispatch(setSelectedOrderReceipt(order));
      setPage(GoodsPage.RECEIPT);
    },
    [dispatch]
  );

  const tableInstance = useWorkTaskOrdersTable(orders, openOrderReceipt);

  if (page === GoodsPage.RECEIPT && order) {
    return (
      <>
        {viewState === 'loading' && <LoadingOverlay />}
        {viewState === 'ready' && (
          <>
            <DialogViewPageHeader
              handleReturn={() => setPage(GoodsPage.STANDARD)}
              title={'Bestilling: ' + order.transferId}
              subHeaderComponent={
                <StatusTag orderStatus={order?.orderStatus as OrderStatus}>
                  {getEnumDisplayValue(order?.orderStatus as OrderStatus)}
                </StatusTag>
              }
            />
            <OrderReceiptContent order={order} />
          </>
        )}
      </>
    );
  }

  if (page === GoodsPage.ADD) {
    return (
      <ConsumeGoodsFromCarPage
        handleReturn={() => setPage(GoodsPage.STANDARD)}
        taskId={taskId}
        projectNumber={project?.id}
        projectLegalEntity={project?.legalEntityId}
      />
    );
  }

  if (page === GoodsPage.CONSUME) {
    return (
      <ConsumeGoodsPage
        handleReturn={() => setPage(GoodsPage.STANDARD)}
        taskId={taskId}
        orders={orders}
        projectId={project?.id ?? ''}
        projectLegalEntityId={projectLegalEntityId}
        legalEntityId={project?.legalEntityId ?? ''}
      />
    );
  }

  return (
    <>
      <Container>
        {viewState === 'loading' && <LoadingOverlay />}
        {viewState === 'ready' && (
          <>
            {userAllowedThroughFeatureFlag(window._env_.ORDER_GOODS_TO_TASK_ENABLED) && (
              <>
                <Header>
                  <DialogViewPageHeader title="Bestillinger til opgaven" />
                  <RightHeaderContainer>
                    {disableOrderGoodsButton ? (
                      <Typography variant="p" color="error">
                        Projektet er i et ugyldigt stadie. Kontakt projektansvarlig for nærmere information.
                      </Typography>
                    ) : (
                      <Button isLoading={loading} onClick={handleOpenOrderGoods} variant="secondary">
                        <ShopIcon size="16px" />
                        Bestil varer til opgaven
                      </Button>
                    )}
                  </RightHeaderContainer>
                </Header>

                <TableClient
                  tableInstance={tableInstance}
                  loading={false}
                  noDataText="Der ligger ingen bestillinger på opgaven"
                  alwaysShowSort
                  noPadding
                  renderFullHeight
                />
              </>
            )}
            <DialogViewPageHeader title="Varer forbrugt på opgaven" />

            <TableContainer>
              <Table>
                <TableHead>
                  <StyledTableRow>
                    <TableCell sx={{ width: '20%' }}>Dato</TableCell>
                    <TableCell sx={{ width: '30%' }}>Lager</TableCell>
                    <TableCell sx={{ width: '30%' }}>Medarbejder</TableCell>
                    <TableCell sx={{ width: '10%' }}>Antal</TableCell>
                    <TableCell sx={{ width: '10%' }}></TableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {consumedItems.length > 0 ? (
                    consumedItems.map((order, i) => {
                      return <GoodsTableRow key={i} itemOrder={order} />;
                    })
                  ) : (
                    <NoDataTableRow text="Ingen varer forbrugt på opgaven" />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
        {viewState === 'error' && <Typography variant="h5"> Der kunne ikke hentes varer.</Typography>}
      </Container>
      <ContentFooterContainer>
        <Footer>
          <FooterLeft>
            {orders.some((order) => order.orderStatus === OrderStatus.Delivered) && (
              <Alert margin="0px 10px 0px 0px" severity="warning" height="42px">
                Du mangler at registrer forbrug fra bestilling
              </Alert>
            )}
          </FooterLeft>
          <FooterRight>
            <ButtonContainer>
              <Button onClick={() => setPage(GoodsPage.ADD)} variant="secondary">
                <AddIcon size="14px" />
                Forbrug varer
              </Button>
              <Button
                disabled={disableConsumeOrderButton}
                onClick={() => setPage(GoodsPage.CONSUME)}
                style={{ margin: '0 10px' }}
              >
                <AddIcon size="14px" />
                Registrer forbrug
              </Button>
            </ButtonContainer>
          </FooterRight>
        </Footer>
      </ContentFooterContainer>
    </>
  );
};

const Container = styled.div`
  position: relative;
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${(props) => props.theme.spacing(5)};
`;

const StyledTableRow = styled((props) => <TableRow {...props} />)`
  && .MuiTableCell-root {
    font-weight: 800;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
`;

export const RightHeaderContainer = styled.div`
  max-width: 300px;
`;

export default TaskGoodsStep;
